import React from 'react';
import { connect } from 'react-redux';
import { PortalContextProvider } from '@amzn/storm-ui';
import { RootState } from 'src/app/store';
import {
  CommunicationsDrawerInfo,
  selectCommunicationsDrawerInfo,
} from 'src/features/header/store/slice';
import { selectViewType } from 'src/app/slice';
import { ErrorBoundary } from 'src/commons/components/ErrorBoundary';
import FederatedModule from './FederatedModule';
import { CommunicationsDrawerPublisher } from 'src/commons/utils/metrics';
import { communicationsDrawerId } from './constants';
import { useAdvertisingApiContext } from 'src/providers/AdvertisingApiProvider';
import { useInternalChromeConfigContext } from 'src/providers/InternalChromeConfigContextProvider';
import { DOMAIN, REALM } from 'src/commons/constants';
import { getTaktAttributes } from 'src/commons/utils/logWithTakt';
import { chromeComponents } from 'src/constants';
import { bellOutline } from '@amzn/storm-ui-icons';
import UtilityButton from 'src/features/header/components/UtilityMenu/UtilityButton';

export const DynamicCommunicationsDrawer: React.VFC<{
  communicationsDrawerInfo?: CommunicationsDrawerInfo;
}> = ({ communicationsDrawerInfo }) => {
  const { advertisingApiClient, advertisingApiCsrfToken } =
    useAdvertisingApiContext();
  const { config: internalChromeConfig } = useInternalChromeConfigContext();

  // render the dynamic drawer when csrf token is available
  const shouldRenderDynamicBellDrawer = advertisingApiCsrfToken !== '';

  const bellEl = React.useRef<HTMLElement>(null);

  const [isCommunicationsDrawerOpen, setIsCommunicationsDrawerOpen] =
    React.useState(false);
  const [showBadge, setShowBadge] = React.useState(false);

  const toggleCommunicationsDrawer = () => {
    CommunicationsDrawerPublisher.publishCounterMonitor(
      isCommunicationsDrawerOpen ? 'Close' : 'Open',
      1,
    );
    setIsCommunicationsDrawerOpen(!isCommunicationsDrawerOpen);
  };

  // todo: refactor this code to only fire when the button is clicked
  // the code below attaches a click listener to the entire dom object
  React.useEffect(() => {
    function onKeyClick({ key }) {
      if (key == 'Escape') {
        toggleCommunicationsDrawer();
      }
    }

    const onClickHandler = ({ target }) => {
      if (target instanceof Node && bellEl && bellEl.current) {
        if (
          bellEl.current === target ||
          bellEl.current.firstChild === target ||
          bellEl.current.firstChild?.contains(target)
        ) {
          //click bell icon should toggle the drawer
          toggleCommunicationsDrawer();
        } else if (
          !bellEl.current.contains(target) &&
          isCommunicationsDrawerOpen
        ) {
          CommunicationsDrawerPublisher.publishCounterMonitor('Close', 1);
          //click anywhere other that bell icon or inside the drawer should close the drawer
          setIsCommunicationsDrawerOpen(false);
        }
      }
    };
    document.addEventListener('mousedown', onClickHandler);

    if (isCommunicationsDrawerOpen) {
      document.addEventListener('keydown', onKeyClick);
      return () => {
        document.removeEventListener('keydown', onKeyClick);
        document.removeEventListener('mousedown', onClickHandler);
      };
    }
    return () => {
      document.removeEventListener('mousedown', onClickHandler);
    };
  }, [isCommunicationsDrawerOpen]);

  if (!communicationsDrawerInfo) {
    return null;
  }

  return (
    <ErrorBoundary name="aces-dynamic-communications-drawer">
      <span ref={bellEl}>
        <PortalContextProvider>
          <UtilityButton
            icon={bellOutline}
            withBadge={showBadge}
            label={communicationsDrawerInfo.texts.label}
            id={communicationsDrawerId}
            className="communications-drawer-button"
            aria-haspopup="true"
            aria-expanded={isCommunicationsDrawerOpen}
            {...getTaktAttributes(
              communicationsDrawerId,
              chromeComponents.HEADER,
            )}
          />
          {shouldRenderDynamicBellDrawer && (
            <FederatedModule
              configs={{
                variant: 'IA2',
                enableCardV2: true,
                advertisingApiClient:
                  advertisingApiClient.getAdvertisingApiClient(),
                consiliorContext: {
                  entityId: internalChromeConfig.entityId,
                  marketplaceId: internalChromeConfig.marketplaceId,
                  locale: internalChromeConfig.locale,
                  isImpersonator: internalChromeConfig.isSpoofer || false,
                  // TODO: Replace with typed context/hook when advertiserId is available through React context
                  advertiserId: (window as any).__AACChrome__?.context
                    ?.advertiserId,
                  domain: DOMAIN['prod'],
                  realm: REALM['us-west-2'],
                },
              }}
              srLabel={communicationsDrawerInfo.texts.label}
              entityId={communicationsDrawerInfo.texts.entityId}
              isOpen={isCommunicationsDrawerOpen}
              onNewNotifications={setShowBadge}
              triggerComponent={bellEl}
            />
          )}
        </PortalContextProvider>
      </span>
    </ErrorBoundary>
  );
};

const acesConnector = connect((state: RootState) => {
  return {
    communicationsDrawerInfo: selectCommunicationsDrawerInfo(state),
    viewType: selectViewType(state),
  };
});

export const ConnectedDynamicCommunicationsDrawer = acesConnector(
  DynamicCommunicationsDrawer,
);
